import { ShapeAttrs } from '@antv/g-base';
import trimEnd from 'lodash/trimEnd';
import { getTextWidth } from './textHelpers';

export interface OptimizeMultilineTextArgs {
  // The number of rows of the text
  maxRows?: number;
  // The width to overflow the text
  maxWidth?: number;
  // The font configuration for the text being measured
  fontConfig?: ShapeAttrs;
}

type AddEllipsisToLine = Pick<OptimizeMultilineTextArgs, 'fontConfig' | 'maxWidth'>;

/**
 * Adds ellipsis to end of line, removing enough characters to avoid overflow
 * @example
 * // returns 'Me no...'
 * addEllipsisToLine('Me non paenitet nullum.', {maxWidth: 5})
 */
export const addEllipsisToLine = (
  text = '',
  { maxWidth = Infinity, fontConfig }: AddEllipsisToLine = {},
) => {
  const ellipsis = '...';

  if (!Number.isFinite(maxWidth)) return `${text}${ellipsis}`;

  const ellipsisWidth = getTextWidth(ellipsis, fontConfig);
  let currTempText = '';
  let currTempTextWidth = 0;

  // eslint-disable-next-line no-restricted-syntax
  for (const char of text) {
    const width = getTextWidth(char, fontConfig);

    if (currTempTextWidth + width > maxWidth - ellipsisWidth) {
      break;
    }

    currTempText += char;
    currTempTextWidth += width;
  }

  return `${currTempText}${ellipsis}`;
};

/**
 * Only add an elipsis to a line if the line is too long
 */
export const addEllipsisToLineIfNeeded = (text = '', opts: AddEllipsisToLine = {}) => {
  if (text.length === 0) return text;
  const { maxWidth = Infinity, fontConfig } = opts;
  return getTextWidth(text, fontConfig) <= maxWidth ? text : addEllipsisToLine(text, opts);
};

type SplitWordFirstHalf = string;
type SplitWordSecondHalf = string;
type Concatenator = string;

interface SplitWordArgs extends OptimizeMultilineTextArgs {
  // The character joining split words
  concatenator?: Concatenator;
}

/**
 * Splits text at a specific max width, including the width of the concatenator
 * NOTE: only measures the first line - not any subsequent lines
 *
 * @example
 * splitWordAtWidth('bobloblaw', {maxWidth: 6, concatenator:'...'})
 * // ['bob', 'loblaw'] ==>  width of 6 ('boblob') minus concatenator width of 3 ('bob')
 */
export const splitWordAtWidth = (
  text: string,
  { maxWidth = Infinity, concatenator = '-', fontConfig }: SplitWordArgs = {},
): [SplitWordFirstHalf, Concatenator, SplitWordSecondHalf] => {
  if (!Number.isFinite(maxWidth) || getTextWidth(text, fontConfig) <= maxWidth) {
    return [text, '', ''];
  }

  const maxWordWidth = maxWidth - getTextWidth(concatenator, fontConfig);
  const chars = text.split('');

  let currTempText = '';
  let currTempTextWidth = 0;
  let splitIndex = 0;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < chars.length; i++) {
    const char = chars[i];
    const width = getTextWidth(char, fontConfig);

    if (currTempTextWidth + width > maxWordWidth) {
      splitIndex = i;
      break;
    }

    currTempText += char;
    currTempTextWidth += width;
  }

  return [currTempText, concatenator, text.slice(splitIndex)];
};

const getIsLastWord = (i = 0, numWords = Infinity): boolean => numWords - 1 <= i;
const getIsLineTooLong = (currentWidth = 0, maxWidth = Infinity): boolean =>
  currentWidth >= maxWidth;

type SplitTextIntoLinesOpts = Pick<OptimizeMultilineTextArgs, 'fontConfig' | 'maxWidth'>;

/**
 * Internal function to split a text row into multiple lines
 * This is internal as it does not take into account '/n'
 */
const splitTextRow = (text = '', opts: SplitTextIntoLinesOpts = {}): string[] => {
  const { maxWidth = Infinity, fontConfig } = opts;

  if (!Number.isFinite(maxWidth) || getTextWidth(text, fontConfig) <= maxWidth) return [text];

  const multilineText = [];
  const textWords = text.split(' ');
  const spaceWidth = getTextWidth(' ', fontConfig);
  let numWords = textWords.length;
  let currentLine = '';
  let currentLineWidth = 0;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < numWords; i++) {
    let word = textWords[i];
    let wordWidth = getTextWidth(word, fontConfig);
    const newLineWidth = currentLineWidth + (currentLine !== '' ? spaceWidth : 0) + wordWidth;

    // check if the line will be too long with the next word
    if (getIsLineTooLong(newLineWidth, maxWidth)) {
      const wordIsTooLong = wordWidth > maxWidth;
      const lineisEmpty = currentLine === '';
      const splitWord = { firstHalf: '', secondHalf: '' };

      // if the word is longer than max width, split it with a dash
      if (wordIsTooLong) {
        // split word at max length (with space for a dash)
        const [firstHalf, concatenator, secondHalf] = splitWordAtWidth(word, {
          maxWidth,
          concatenator: '-',
        });
        splitWord.firstHalf = `${firstHalf}${concatenator}`;
        splitWord.secondHalf = secondHalf;
        // insert second half of word at the next index in the array
        textWords.splice(i + 1, 0, splitWord.secondHalf);
        numWords += 1;
        // unset word and wordWidth so that it doesn't get added to the next line
        word = '';
        wordWidth = 0;
      }

      // If the line is empty, the first half of the split word should be added
      if (wordIsTooLong && lineisEmpty) {
        currentLine += splitWord.firstHalf;
      }

      multilineText.push(currentLine);
      currentLine = '';
      currentLineWidth = 0;

      // If the line already has words, push the first word to a new line
      if (wordIsTooLong && !lineisEmpty) {
        multilineText.push(splitWord.firstHalf);
      }
    }

    const shouldAddSpace = currentLine !== '';
    currentLine += `${shouldAddSpace ? ' ' : ''}${word}`;
    currentLineWidth += (shouldAddSpace ? spaceWidth : 0) + wordWidth;

    // If this is the last line and we cannot fit anymore words, add an ellipsis
    // to the remaining text (if needed) and add it to the lines
    if (
      // do we either have too much text, or are we on the last word?
      getIsLineTooLong(currentLineWidth, maxWidth) ||
      getIsLastWord(i, textWords.length)
    ) {
      multilineText.push(currentLine);
      currentLine = '';
      currentLineWidth = 0;
    }
  }

  return multilineText;
};

/**
 * Splits string into an array - each item having a max width of `maxWidth`
 * in html canvas
 *
 * @example
 * splitTextIntoLines('Me non paenitet nullum.', {maxWidth: 5})
 * // returns ['Me non', 'paenit', 'et nul', 'lum.']
 */
export const splitTextIntoLines = (text = '', opts: SplitTextIntoLinesOpts = {}): string[] =>
  text.split('\n').reduce((acc: string[], row) => {
    const lines = splitTextRow(row, opts);
    return [...acc, ...(lines.length ? lines : [''])];
  }, []);

/**
 * Splits text to a given amount of lines, and adds an ellipsis if needed
 * @example
 * // returns 'Me no...'
 * optimizeMultilineText('Me non paenitet nullum.', {maxWidth: 5})
 * @example
 * // returns 'Me no\n paen...'
 * optimizeMultilineText('Me non paenitet nullum.', {maxWidth: 5, maxRows:2})
 */
export const optimizeMultilineText = (text = '', opts: OptimizeMultilineTextArgs = {}): string => {
  const { fontConfig, maxWidth = Infinity, maxRows = Infinity } = opts;

  if (!Number.isFinite(maxWidth)) return text;

  const textIsTooLong = getTextWidth(text, fontConfig) > maxWidth;
  const textIsTooTall = text.split('\n').length > maxRows;

  if (!textIsTooLong && !textIsTooTall) return text;

  let rows = splitTextIntoLines(text, opts);

  if (rows.length > maxRows) {
    const visibleRows = rows.splice(0, maxRows);
    const lastVisibleRow = visibleRows.pop();
    // Add ellipsis and remove final dash (in case of split word)
    visibleRows.push(addEllipsisToLine(trimEnd(lastVisibleRow, '-')));
    rows = visibleRows;
  }
  return rows.join('\n');
};
